import clsx from 'clsx';
import React, { useCallback } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import { media } from '@ovpn-ui/styles';
import { Modal, Button, ButtonGroup } from '@ovpn-ui/core';

import { prompt, portalReady } from './slice';
import { selectCurrentType, selectIsOpened, selectModalProps } from './selectors';

export const PROMPT_ROOT = '@@prompt/prompt-root';

const BulkPanelHack = createGlobalStyle`
    .ovpn-table-bulk-actions-panel {
        z-index: 1000 !important;
    }
`;

const Buttons = styled(ButtonGroup)`
    ${media.XS`
        flex-direction: column
    `}
`;

export default function PromptContainer() {
    const dispatch = useDispatch();
    const open = useSelector(selectIsOpened);

    const props = useSelector(selectModalProps);
    const type = useSelector(selectCurrentType);

    const {
        title,
        content,
        isLoading,
        size,
        variant = 'default',

        confirmText,
        submitProps,
        cancelText = 'Cancel',
        cancelProps,
        onCancel,
        onSubmit: userSubmitHandler,
        onNoAction,

        ...modalProps
    } = props;

    const handleRef = useCallback(
        ref => {
            if (ref) {
                dispatch(portalReady());
            }
        },
        [dispatch],
    );

    const onClose = useCallback(() => {
        onNoAction?.();

        return dispatch(prompt.failure());
    }, [dispatch, onNoAction]);

    const onSubmit = useCallback(() => {
        if (userSubmitHandler) {
            userSubmitHandler();
        } else {
            dispatch(prompt.success());
        }
    }, [dispatch, userSubmitHandler]);

    const keyHandler = useCallback(
        (e: any) => {
            if (e.key === 'Enter') onSubmit();
        },
        [onSubmit],
    );

    const hideFooter = modalProps.footer === null;

    return (
        <>
            {open && <BulkPanelHack />}
            <Modal
                open={open}
                size={size ?? 'lg'}
                id={type}
                closeOnClickOutside
                {...modalProps}
                onClose={onClose}
                onKeyDown={keyHandler}
                title={title}
                footer={
                    !hideFooter &&
                    (typeof modalProps.footer === 'function' ? (
                        modalProps.footer({ onSubmit, onClose, isLoading, Wrapper: Buttons })
                    ) : (
                        <Buttons fullWidth>
                            <Button
                                onClick={onSubmit}
                                isLoading={isLoading}
                                variant={variant === 'danger' ? 'warning' : 'primary'}
                                {...submitProps}
                                className={clsx('ovpn-submit-button', submitProps?.className)}
                                id={clsx('btn-submit', submitProps?.id)}
                            >
                                {confirmText}
                            </Button>
                            {!!cancelText && (
                                <Button
                                    variant="ghost"
                                    onClick={onCancel || onClose}
                                    {...cancelProps}
                                    className={clsx('ovpn-cancel-button', cancelProps?.className)}
                                    id={clsx('btn-cancel', cancelProps?.id)}
                                >
                                    {cancelText}
                                </Button>
                            )}
                        </Buttons>
                    ))
                }
            >
                {content}
                <div id={PROMPT_ROOT} ref={handleRef} />
            </Modal>
        </>
    );
}
